import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { PageHeader } from '@shared/components/PageHeader';
import {
  normalizeShopTaxonomy,
  validateShopTaxonomy,
} from '@features/settings/ShopTaxonomyTab';
import type { ShopTaxonomy } from '@shared/api/hooks/useTenantAdminConfig';
import {
  useShopSettings,
  useUpdateShopSettings,
} from '@shared/api/hooks/useShopSettings';
import { useProducts, type ProductDto } from '@shared/api/hooks/useProducts';

// ─── Utilità ──────────────────────────────────────────────────────────────────

/** Etichetta leggibile per un prodotto (name.it, fallback sku). */
function productLabel(name: unknown, sku: string): string {
  if (name && typeof name === 'object' && 'it' in name) {
    const it = (name as { it?: unknown }).it;
    if (typeof it === 'string' && it.trim()) return it;
  }
  return sku;
}

// ─── Componente principale ────────────────────────────────────────────────────

export function ShopSettingsPage(): React.ReactElement {
  const { data, isLoading, refetch, isFetching } = useShopSettings();
  const updateMutation = useUpdateShopSettings();

  // Prodotti per il picker "in primo piano".
  const { data: productsData } = useProducts({ page: 1, limit: 100 });
  const products: ProductDto[] = productsData?.data ?? [];

  // Draft locale della tassonomia + prodotto in primo piano.
  const [draft, setDraft] = useState<ShopTaxonomy>(() => normalizeShopTaxonomy(data?.taxonomy));
  // T-81: lista ORDINATA di prodotti in primo piano (l'ordine è di visualizzazione).
  const [featuredIds, setFeaturedIds] = useState<string[]>([]);

  // Sincronizza il draft quando arrivano/aggiornano i dati dal server.
  useEffect(() => {
    if (data) {
      setDraft(normalizeShopTaxonomy(data.taxonomy));
      setFeaturedIds(data.featuredProductIds ?? []);
    }
  }, [data]);

  const inactive = draft.inactiveMacrocategories ?? [];

  // ── Mutazioni sul draft ─────────────────────────────────────────────────────

  const moveMacro = (index: number, dir: -1 | 1): void => {
    const target = index + dir;
    if (target < 0 || target >= draft.macrocategories.length) return;
    const macros = [...draft.macrocategories];
    [macros[index], macros[target]] = [macros[target], macros[index]];
    setDraft({ ...draft, macrocategories: macros });
  };

  const renameMacro = (oldName: string, rawNext: string): void => {
    const next = rawNext.trim();
    if (!next || next === oldName) return;
    // Evita collisioni con una macro esistente.
    if (draft.macrocategories.includes(next)) {
      message.error(`La categoria "${next}" esiste già.`);
      return;
    }
    const macros = draft.macrocategories.map((m) => (m === oldName ? next : m));
    const sub: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(draft.subcategoriesByMacro)) {
      sub[k === oldName ? next : k] = v;
    }
    const inact = inactive.map((m) => (m === oldName ? next : m));
    setDraft({
      ...draft,
      macrocategories: macros,
      subcategoriesByMacro: sub,
      inactiveMacrocategories: inact,
    });
  };

  const toggleMacroActive = (macro: string, active: boolean): void => {
    const inact = active
      ? inactive.filter((m) => m !== macro)
      : inactive.includes(macro)
        ? inactive
        : [...inactive, macro];
    setDraft({ ...draft, inactiveMacrocategories: inact });
  };

  // ── Featured: lista ordinata (T-81), stesso pattern di riordino delle macro ──
  const addFeatured = (id: string): void => {
    if (!id || featuredIds.includes(id)) return; // dedup silenzioso
    setFeaturedIds([...featuredIds, id]);
  };
  const removeFeatured = (id: string): void => {
    setFeaturedIds(featuredIds.filter((x) => x !== id));
  };
  const moveFeatured = (index: number, dir: -1 | 1): void => {
    const target = index + dir;
    if (target < 0 || target >= featuredIds.length) return;
    const ids = [...featuredIds];
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setFeaturedIds(ids);
  };

  const setSubcategories = (macro: string, subs: string[]): void => {
    setDraft({
      ...draft,
      subcategoriesByMacro: { ...draft.subcategoriesByMacro, [macro]: subs },
    });
  };

  const addMacro = (): void => {
    // Trova un nome libero "Nuova categoria" / "Nuova categoria 2" / …
    const base = 'Nuova categoria';
    let name = base;
    let i = 2;
    while (draft.macrocategories.includes(name)) {
      name = `${base} ${i}`;
      i += 1;
    }
    setDraft({
      ...draft,
      macrocategories: [...draft.macrocategories, name],
      subcategoriesByMacro: { ...draft.subcategoriesByMacro, [name]: [] },
    });
  };

  const setSizeSet = (band: 'senior' | 'junior', sizes: string[]): void => {
    setDraft({ ...draft, sizeSets: { ...draft.sizeSets, [band]: sizes } });
  };

  // ── Salvataggio ─────────────────────────────────────────────────────────────

  const onSave = async (): Promise<void> => {
    const errors = validateShopTaxonomy(draft);
    if (errors.length > 0) {
      message.error(errors[0]);
      return;
    }
    try {
      await updateMutation.mutateAsync({ taxonomy: draft, featuredProductIds: featuredIds });
      message.success('Impostazioni Shop salvate.');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore salvataggio'));
    }
  };

  if (isLoading) {
    return (
      <Card>
        <Spin />
      </Card>
    );
  }

  // Risoluzione display dei featured: prima il catalogo locale, poi la lista
  // risolta dal server (che porta il flag `missing`), infine fallback "missing".
  const productById = new Map(products.map((p) => [p.id, p]));
  const resolvedById = new Map((data?.featuredProducts ?? []).map((fp) => [fp.id, fp]));
  const resolveFeatured = (
    id: string,
  ): { label: string; sku: string | null; available: boolean; missing: boolean } => {
    const p = productById.get(id);
    if (p) return { label: productLabel(p.name, p.sku), sku: p.sku, available: p.isAvailable, missing: false };
    const fp = resolvedById.get(id);
    if (fp && !fp.missing) {
      return { label: productLabel(fp.name, fp.sku ?? id), sku: fp.sku, available: fp.isAvailable, missing: false };
    }
    return { label: id, sku: null, available: false, missing: true };
  };

  return (
    <>
      <PageHeader
        title="Impostazioni Shop"
        subtitle="Gestisci le categorie merceologiche del catalogo e scegli il prodotto in primo piano mostrato in app."
        extra={
          <>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
              Ricarica
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={onSave}
              loading={updateMutation.isPending}
            >
              Salva
            </Button>
          </>
        }
      />

      <div style={{ padding: 24 }}>
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          {/* ── Categorie merceologiche ─────────────────────────────────────── */}
          <Card
            size="small"
            title="Categorie merceologiche"
            extra={
              <Button size="small" icon={<PlusOutlined />} onClick={addMacro}>
                Aggiungi categoria
              </Button>
            }
          >
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Ordina, rinomina e attiva/disattiva le macro-categorie. Una categoria disattivata non
              viene eliminata: resta salvata ma non è mostrata in app. Per ciascuna categoria
              definisci le sotto-categorie (digita e premi Invio).
            </Typography.Text>

            {draft.macrocategories.length === 0 ? (
              <Empty description="Nessuna categoria. Aggiungine una per iniziare." />
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                {draft.macrocategories.map((macro, index) => {
                  const isActive = !inactive.includes(macro);
                  return (
                    <Row key={macro} gutter={12} align="middle" wrap={false}>
                      <Col flex="none">
                        <Space size={2} direction="vertical">
                          <Button
                            size="small"
                            aria-label={`Sposta su ${macro}`}
                            disabled={index === 0}
                            onClick={() => moveMacro(index, -1)}
                          >
                            ↑
                          </Button>
                          <Button
                            size="small"
                            aria-label={`Sposta giù ${macro}`}
                            disabled={index === draft.macrocategories.length - 1}
                            onClick={() => moveMacro(index, 1)}
                          >
                            ↓
                          </Button>
                        </Space>
                      </Col>
                      <Col flex="220px">
                        <Input
                          aria-label={`Nome categoria ${macro}`}
                          defaultValue={macro}
                          onBlur={(e) => renameMacro(macro, e.target.value)}
                          onPressEnter={(e) =>
                            renameMacro(macro, (e.target as HTMLInputElement).value)
                          }
                        />
                      </Col>
                      <Col flex="none">
                        <Switch
                          aria-label={`Attiva ${macro}`}
                          checked={isActive}
                          checkedChildren="Attiva"
                          unCheckedChildren="Disattiva"
                          onChange={(checked) => toggleMacroActive(macro, checked)}
                        />
                      </Col>
                      <Col flex="auto">
                        <Select
                          mode="tags"
                          style={{ width: '100%' }}
                          placeholder={`Sotto-categorie di ${macro}…`}
                          value={draft.subcategoriesByMacro[macro] ?? []}
                          onChange={(v) => setSubcategories(macro, v)}
                          tokenSeparators={[',']}
                          open={false}
                        />
                      </Col>
                    </Row>
                  );
                })}
              </Space>
            )}
          </Card>

          {/* ── Set taglie ──────────────────────────────────────────────────── */}
          <Card size="small" title="Set di taglie">
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Typography.Text strong>Senior</Typography.Text>
                <Select
                  mode="tags"
                  style={{ width: '100%', marginTop: 6 }}
                  placeholder="XS, S, M, L, XL, XXL…"
                  value={draft.sizeSets.senior}
                  onChange={(v) => setSizeSet('senior', v)}
                  tokenSeparators={[',']}
                  open={false}
                />
              </Col>
              <Col xs={24} md={12}>
                <Typography.Text strong>Junior</Typography.Text>
                <Select
                  mode="tags"
                  style={{ width: '100%', marginTop: 6 }}
                  placeholder="5/6, 7/8, 9/10…"
                  value={draft.sizeSets.junior}
                  onChange={(v) => setSizeSet('junior', v)}
                  tokenSeparators={[',']}
                  open={false}
                />
              </Col>
            </Row>
          </Card>

          {/* ── Prodotti in primo piano (lista ordinata, T-81) ───────────────── */}
          <Card size="small" title="Prodotti in primo piano">
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              I prodotti scelti sono messi in evidenza nella vetrina dello shop in app,
              nell&apos;ordine indicato qui sotto. Lascia la lista vuota per non evidenziare nulla.
            </Typography.Text>
            <Select
              showSearch
              style={{ width: '100%', maxWidth: 480 }}
              placeholder="Aggiungi un prodotto in primo piano…"
              value={null}
              onChange={(v) => v && addFeatured(v)}
              optionFilterProp="label"
              options={products
                .filter((p) => p.isAvailable && !featuredIds.includes(p.id))
                .map((p) => ({ value: p.id, label: productLabel(p.name, p.sku) }))}
              notFoundContent={
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nessun prodotto disponibile" />
              }
            />

            {featuredIds.length > 8 && (
              <Alert
                style={{ marginTop: 12 }}
                type="warning"
                showIcon
                message={`Hai ${featuredIds.length} prodotti in primo piano: valuta se sono troppi per la vetrina.`}
              />
            )}

            <div style={{ marginTop: 12 }}>
              {featuredIds.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Nessun prodotto in primo piano"
                />
              ) : (
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  {featuredIds.map((id, index) => {
                    const info = resolveFeatured(id);
                    return (
                      <Row
                        key={id}
                        align="middle"
                        gutter={8}
                        wrap={false}
                        style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: '6px 8px' }}
                      >
                        <Col flex="none">
                          <Space size={2} direction="vertical">
                            <Button
                              size="small"
                              type="text"
                              disabled={index === 0}
                              onClick={() => moveFeatured(index, -1)}
                              aria-label="Sposta su"
                            >
                              ↑
                            </Button>
                            <Button
                              size="small"
                              type="text"
                              disabled={index === featuredIds.length - 1}
                              onClick={() => moveFeatured(index, 1)}
                              aria-label="Sposta giù"
                            >
                              ↓
                            </Button>
                          </Space>
                        </Col>
                        <Col flex="none">
                          <Tag>{index + 1}</Tag>
                        </Col>
                        <Col flex="auto">
                          <Space wrap size={4}>
                            <Typography.Text strong>{info.label}</Typography.Text>
                            {info.sku && <Tag>{info.sku}</Tag>}
                            {info.missing ? (
                              <Tag color="red">Non più disponibile</Tag>
                            ) : (
                              <Tag color={info.available ? 'green' : 'orange'}>
                                {info.available ? 'Disponibile' : 'Non disponibile'}
                              </Tag>
                            )}
                          </Space>
                        </Col>
                        <Col flex="none">
                          <Button size="small" danger type="text" onClick={() => removeFeatured(id)}>
                            Rimuovi
                          </Button>
                        </Col>
                      </Row>
                    );
                  })}
                </Space>
              )}
            </div>
          </Card>
        </Space>
      </div>
    </>
  );
}
